import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { FC, useEffect, useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Fade } from '@column/column-ui-kit';

import { CustomPageContext, TabbedPage } from '~/app/Layout';
import { ROUTE } from '~/app/routes';
import { InlineCopyInput, PageHeader } from '~/components';
import { LogoLoading } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import { Card, CardRepository } from '~/repositories/CardRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Inner, Line, Text } from '~/styles';

interface Params {
  id: string;
}

export interface CardProps {
  card: Card;
}

const TabNavigation = styled(TabbedPage)`
  margin: 0;
`;

const StyledFade = styled(Fade)`
  margin-top: 10%;
`;

const CardHeaderRow = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const InlineCopyInputWrapper = styled.div`
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    width: auto;
  }
`;

const cardQueryKey = (id: string) => ['card', id] as const;

export const PageCard: FC<CustomPageContext> = ({ entries }) => {
  const navigate = useNavigate();
  const { currentUser } = useSessionStore();
  const { addDangerNotification } = useNotificationStore();
  const queryClient = useQueryClient();
  const { id } = useParams<keyof Params>() as Params;

  const {
    data: card,
    isLoading,
    error: cardError,
  } = useQuery({
    queryKey: cardQueryKey(id!),
    queryFn: () => CardRepository.get(id!),
    enabled: !!id,
  });

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => ({
          isSandbox: state.isSandbox,
          isLoading: state.isLoading,
          defaultPlatformId: state.currentUser?.defaultPlatformId,
        }),
        (newState, oldState) => {
          if (!currentUser) {
            return;
          }

          if (newState.isSandbox !== oldState.isSandbox || newState.defaultPlatformId !== oldState.defaultPlatformId) {
            if (id) {
              queryClient.invalidateQueries({ queryKey: cardQueryKey(id) });
            }
          }
        }
      ),
    [id, currentUser, queryClient]
  );

  useEffect(() => {
    if (cardError) {
      navigate(ROUTE.CARDS);
      addDangerNotification({
        content: (cardError as any).message || 'Failed to load card',
      });
    }
  }, [cardError, navigate, addDangerNotification]);

  const outletContext = useMemo(() => (card ? { card } : undefined), [card]);

  const formatCardTitle = (cardData: Card): string => {
    if (cardData.bin && cardData.lastFourDigits) {
      return `Card ${cardData.bin}••••${cardData.lastFourDigits}`;
    }
    if (cardData.lastFourDigits) {
      return `Card ••••${cardData.lastFourDigits}`;
    }
    return 'Card';
  };

  return (
    <>
      <StyledFade show={isLoading || !card}>
        <LogoLoading />
      </StyledFade>
      <Fade show={!isLoading && !!card}>
        <PageHeader text="Cards" backPath={ROUTE.CARDS} tabs={<TabNavigation entries={entries} />} backButton />
        {id && card && (
          <>
            <Inner pt={12} px="var(--page-margin)" pb={16}>
              <Flex flexDirection="column" gap={12}>
                <CardHeaderRow>
                  <Text as="h2" size={16} letterSpacing="tight" weight="medium" color="foreground">
                    {formatCardTitle(card)}
                  </Text>

                  <InlineCopyInputWrapper>
                    <InlineCopyInput label="Card ID" value={card.id} minWidth="300px" isMonospaced />
                  </InlineCopyInputWrapper>
                </CardHeaderRow>
              </Flex>
            </Inner>
            <Line />
          </>
        )}
        {outletContext && <Outlet context={outletContext} />}
      </Fade>
    </>
  );
};
